import type { FC, PropsWithChildren, ReactNode } from 'react';
import { useMemo } from 'react';
import cx from 'classnames';
import { Heading, LabeledList } from '@george-labs.com/design-system';
import type {
    LABELED_LIST_ALIGNMENT,
    LABELED_LIST_LABEL_SIZE,
    LABELED_LIST_VARIANT,
} from '@george-labs.com/design-system/types/components/LabeledList';
import type { ColSize } from '@george-labs.com/design-system/types/components/Layout';

import { t } from '@luna/i18n';
import { uuid } from '@luna/utils';

import { ProductsListContext } from './ProductsListContext';
import { ProductsListAmountItem } from './items/ProductsListAmountItem';
import { ProductsListCustomItem } from './items/ProductsListCustomItem';
import { ProductsListDateItem } from './items/ProductsListDateItem';
import { ProductsListDateRangeItem } from './items/ProductsListDateRangeItem';
import { ProductsListMultipleTextItem } from './items/ProductsListMultipleTextItem';
import { ProductsListPercentageItem } from './items/ProductsListPercentageItem';
import { ProductsListTextItem } from './items/ProductsListTextItem';

type Props = {
    'data-cy'?: string;
    className?: string;
    variant?: `${LABELED_LIST_VARIANT}`;
    alignment?: `${LABELED_LIST_ALIGNMENT}`;
    labelSize?: `${LABELED_LIST_LABEL_SIZE}`;
    labelClassName?: string;
    labelColumnWidth?: ColSize;
    noPadding?: true;
    noPaddingForHeading?: boolean;
} & (SimpleList | TitledList);

type SimpleList = {
    showHeading?: false;
    title?: never;
};

type TitledList = {
    showHeading: true;
    title?: ReactNode;
};

type ListItems = {
    AmountItem: typeof ProductsListAmountItem;
    CustomItem: typeof ProductsListCustomItem;
    DateItem: typeof ProductsListDateItem;
    DateRangeItem: typeof ProductsListDateRangeItem;
    MultipleTextItem: typeof ProductsListMultipleTextItem;
    PercentageItem: typeof ProductsListPercentageItem;
    TextItem: typeof ProductsListTextItem;
};

const DEFAULT_LABEL_COLUMN_WIDTH = 6;

export const ProductsList: FC<PropsWithChildren<Props>> & ListItems = ({
    children,
    'data-cy': dataCy,
    className,
    variant = 'plain',
    alignment,
    labelSize,
    labelClassName,
    labelColumnWidth = DEFAULT_LABEL_COLUMN_WIDTH,
    noPadding = false,
    noPaddingForHeading = false,
    showHeading,
    title = t('account.main.productDetails'),
}) => {
    const headingId = useMemo(() => (showHeading ? uuid() : undefined), [showHeading]);

    const providerValue = useMemo(
        () => ({ labelClassName, labelColumnWidth }),
        [labelClassName, labelColumnWidth],
    );

    const headingClassName = noPaddingForHeading ? 'g-h3' : 'pl-3 g-h3';

    return (
        <ProductsListContext.Provider value={providerValue}>
            {showHeading && (
                <Heading
                    id={headingId}
                    className={headingClassName}
                    level={2}
                    data-cy={dataCy && `${dataCy}-header`}
                >
                    {title}
                </Heading>
            )}
            <LabeledList
                data-cy={dataCy}
                className={cx(className, { 'p-0': noPadding })}
                variant={variant as LABELED_LIST_VARIANT}
                alignment={alignment as LABELED_LIST_ALIGNMENT}
                labelSize={labelSize as LABELED_LIST_LABEL_SIZE}
                labelClassName={labelClassName}
                labelColumnWidth={labelColumnWidth}
                aria-labelledby={headingId}
            >
                {children}
            </LabeledList>
        </ProductsListContext.Provider>
    );
};

ProductsList.AmountItem = ProductsListAmountItem;
ProductsList.CustomItem = ProductsListCustomItem;
ProductsList.DateItem = ProductsListDateItem;
ProductsList.DateRangeItem = ProductsListDateRangeItem;
ProductsList.MultipleTextItem = ProductsListMultipleTextItem;
ProductsList.PercentageItem = ProductsListPercentageItem;
ProductsList.TextItem = ProductsListTextItem;
